import React, { useEffect, useState } from "react";
import {
    Form, FormGroup, Label, Input, Row, Col, Button, FormFeedback
} from "reactstrap";
import Session from '../../auth/Session';

// One-off manual pay line on a paysheet (e.g. a run missed in a prior week). Mirrors
// InvoiceAdjustmentForm: the user enters a description, quantity, and total amount, and picks a
// transaction type whose IsPositiveAdjustment sets the sign (pay vs. deduct).
const PaySheetAdjustmentForm = ({ paySheetId, adjustment, onSuccess, onCancel }) => {
    const isEdit = !!adjustment;
    const isCommentLine = adjustment?.detailType === "Comment";
    const [formData, setFormData] = useState({
        description: adjustment?.description ?? "",
        amount: adjustment != null && adjustment.routeTotal != null ? `${Math.abs(adjustment.routeTotal)}` : "",
        quantity: adjustment != null && adjustment.daysOnRoute != null ? `${adjustment.daysOnRoute}` : "1",
        transactionTypeId: adjustment?.transactionTypeId != null ? `${adjustment.transactionTypeId}` : "",
        isComment: isCommentLine,
    });
    const [transactionTypes, setTransactionTypes] = useState([]);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const load = async () => {
            const token = await Session.getToken();
            const res = await fetch("/api/paysheets/transaction-types", {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                setTransactionTypes(await res.json());
            }
        };
        load();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(p => ({ ...p, [name]: value }));
    };

    const validate = () => {
        const v = {};
        if (!formData.description.trim()) v.description = "Description is required";
        // Comment-only lines carry no amount/quantity/transaction type; skip those checks.
        if (!formData.isComment) {
            const amount = Number(formData.amount);
            if (!formData.amount || isNaN(amount) || amount <= 0) v.amount = "Amount must be greater than zero";
            const quantity = Number(formData.quantity);
            if (!formData.quantity || isNaN(quantity) || quantity <= 0) v.quantity = "Quantity must be greater than zero";
            if (!formData.transactionTypeId) v.transactionTypeId = "Transaction type is required";
        }
        setErrors(v);
        return Object.keys(v).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            const token = await Session.getToken();
            const url = isEdit ? "/api/paysheets/update-adjustment" : "/api/paysheets/add-adjustment";
            // For comment-only lines the backend ignores amount/quantity/type, so send neutral values.
            const amount = formData.isComment ? 0 : Number(formData.amount);
            const quantity = formData.isComment ? 0 : Number(formData.quantity);
            const transactionTypeId = formData.isComment || !formData.transactionTypeId
                ? null
                : Number(formData.transactionTypeId);
            const body = isEdit
                ? {
                    paySheetId: paySheetId,
                    paySheetDetailId: adjustment.id,
                    description: formData.description.trim(),
                    amount: amount,
                    quantity: quantity,
                    transactionTypeId: transactionTypeId,
                    isComment: formData.isComment,
                }
                : {
                    paySheetId: paySheetId,
                    description: formData.description.trim(),
                    amount: amount,
                    quantity: quantity,
                    transactionTypeId: transactionTypeId,
                    isComment: formData.isComment,
                };
            const res = await fetch(url, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const t = await res.text();
                alert(`Save failed: ${res.status} ${t}`);
                return;
            }
            onSuccess(await res.json());
        } finally {
            setSubmitting(false);
        }
    };

    const selectedType = transactionTypes.find(t => `${t.id}` === `${formData.transactionTypeId}`);

    return (
        <Form onSubmit={handleSubmit}>
            <Row>
                <Col md={12}>
                    <FormGroup>
                        <Label>Description</Label>
                        <Input
                            name="description"
                            type="text"
                            maxLength={500}
                            value={formData.description}
                            onChange={handleChange}
                            invalid={!!errors.description}
                        />
                        <FormFeedback>{errors.description}</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    <FormGroup check className="mb-3">
                        <Input
                            id="paysheet-adjustment-is-comment"
                            name="isComment"
                            type="checkbox"
                            checked={formData.isComment}
                            onChange={(e) => setFormData(p => ({ ...p, isComment: e.target.checked }))}
                        />
                        <Label check for="paysheet-adjustment-is-comment">Comment only (no amount)</Label>
                    </FormGroup>
                </Col>
            </Row>
            {!formData.isComment && (
            <Row>
                <Col md={4}>
                    <FormGroup>
                        <Label>Quantity</Label>
                        <Input
                            name="quantity"
                            type="number"
                            step="any"
                            min="0"
                            value={formData.quantity}
                            onChange={handleChange}
                            invalid={!!errors.quantity}
                        />
                        <FormFeedback>{errors.quantity}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup>
                        <Label>Amount</Label>
                        <Input
                            name="amount"
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.amount}
                            onChange={handleChange}
                            invalid={!!errors.amount}
                        />
                        <FormFeedback>{errors.amount}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup>
                        <Label>Transaction Type</Label>
                        <Input
                            name="transactionTypeId"
                            type="select"
                            value={formData.transactionTypeId}
                            onChange={handleChange}
                            invalid={!!errors.transactionTypeId}
                        >
                            <option value="">-- Transaction Type --</option>
                            {transactionTypes.map(t => (
                                <option key={t.id} value={t.id}>
                                    {t.transactionName}{t.isPositiveAdjustment === false ? " (deduction)" : ""}
                                </option>
                            ))}
                        </Input>
                        <FormFeedback>{errors.transactionTypeId}</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>
            )}
            {!formData.isComment && selectedType && (
                <div className="text-muted small mb-3">
                    This will be recorded as {selectedType.isPositiveAdjustment === false ? "a deduction (negative)" : "an addition (positive)"} on the paysheet.
                </div>
            )}
            <div className="d-flex justify-content-end gap-2">
                <Button color="secondary" type="button" onClick={onCancel} disabled={submitting}>Cancel</Button>
                <Button color="primary" type="submit" disabled={submitting}>
                    {submitting ? "Saving…" : isEdit ? "Save Adjustment" : "Add Adjustment"}
                </Button>
            </div>
        </Form>
    );
};

export default PaySheetAdjustmentForm;
